import React, { useEffect, useMemo, useState } from 'react';
import { AlertCircle, ArrowLeft, ArrowRight, Check, FileText, TrendingUp } from 'lucide-react';
import { AudienceType, BrandProfile, CampaignGoal, CampaignIndustry, DialectStyle, ProjectFormData, PropertyType } from '../types';
import { EGYPT_REGIONS, PRESET_TEMPLATES } from '../data/presets';
import { useToast } from '../contexts/ToastContext';
import { useTranslation } from '../i18n/translations';
import { INDUSTRY_BENCHMARKS } from '../data/industryBenchmarks';
import { api } from '../utils/api';

interface ProjectFormProps {
  onSubmit: (data: ProjectFormData) => void;
  isLoading: boolean;
  remainingCredits: number;
  currentPlan?: 'free' | 'pro' | 'agency';
  brandProfile?: BrandProfile;
}

const DEFAULT_DATA: ProjectFormData = {
  industry: 'real_estate',
  projectName: '',
  developerName: '',
  propertyType: 'residential',
  region: EGYPT_REGIONS[0],
  subArea: '',
  unitType: '',
  startingPrice: 5000000,
  currency: 'EGP',
  downPaymentPercent: 10,
  installmentYears: 8,
  deliveryYear: '2026',
  keyFeatures: [],
  audienceType: 'egyptian_investor',
  targetAudience: '',
  customerPainPoint: '',
  promotionDetails: '',
  conversionChannel: 'whatsapp',
  fulfillmentDetails: '',
  websiteUrl: '',
  trustSignals: '',
  scheduleDetails: '',
  averageOrderValue: 0,
  dialectStyle: 'egyptian_colloquial',
  monthlyBudgetEGP: 30000,
  campaignGoal: 'launch_sales',
};

const REAL_ESTATE_GOALS: Array<{ value: CampaignGoal; title: string; description: string }> = [
  { value: 'launch_sales', title: 'بيع وحدات خلال فترة الطرح', description: 'بناء طلب واضح وتحويل الاهتمام إلى تسجيلات جادة' },
  { value: 'whatsapp_leads', title: 'حملة واتساب', description: 'محادثات مباشرة مع فريق المبيعات' },
  { value: 'gulf_investors', title: 'جذب مستثمرين من الخليج', description: 'رسالة استثمارية تناسب الجمهور الخليجي' },
  { value: 'retargeting', title: 'إعادة استهداف العملاء القدامى', description: 'إعادة تنشيط العملاء الذين أبدوا اهتمامًا سابقًا' },
  { value: 'prelaunch_interest', title: 'تسجيل اهتمام قبل الإطلاق', description: 'تكوين قائمة انتظار قبل فتح البيع' },
  { value: 'remaining_inventory', title: 'بيع الوحدات المتبقية', description: 'معالجة الاعتراضات وتحريك المخزون المتبقي' },
  { value: 'ready_to_move', title: 'مشروع جاهز للاستلام', description: 'التركيز على الجاهزية وتقليل وقت قرار الشراء' },
];

const GENERAL_GOALS: Array<{ value: CampaignGoal; title: string; description: string }> = [
  { value: 'sales', title: 'زيادة المبيعات', description: 'تحويل الجمهور إلى طلبات شراء مباشرة' },
  { value: 'lead_generation', title: 'جمع عملاء محتملين', description: 'تجميع بيانات أشخاص مناسبين لفريق المبيعات' },
  { value: 'messages', title: 'محادثات واتساب أو ماسنجر', description: 'بدء محادثات مباشرة مع العملاء المحتملين' },
  { value: 'bookings', title: 'حجوزات ومواعيد', description: 'زيادة حجوزات الفروع أو الجلسات أو الاستشارات' },
  { value: 'website_traffic', title: 'زيارات الموقع أو المتجر', description: 'توجيه جمهور مؤهل إلى صفحة المنتج أو الخدمة' },
  { value: 'brand_awareness', title: 'انتشار ووعي بالعلامة', description: 'تعريف جمهور جديد بالعلامة وعرضها الأساسي' },
  { value: 'retargeting', title: 'إعادة استهداف العملاء السابقين', description: 'إعادة تنشيط الزوار والعملاء الذين تفاعلوا من قبل' },
  { value: 'store_visits', title: 'زيارات المتجر أو الفرع', description: 'زيادة الزيارات الفعلية إلى أقرب فرع أو موقع خدمة' },
];

const GOALS_BY_INDUSTRY: Partial<Record<CampaignIndustry, CampaignGoal[]>> = {
  ecommerce: ['sales', 'website_traffic', 'messages', 'retargeting', 'brand_awareness'],
  professional_services: ['lead_generation', 'messages', 'bookings', 'website_traffic', 'brand_awareness', 'retargeting'],
  education: ['lead_generation', 'sales', 'messages', 'website_traffic', 'brand_awareness', 'retargeting'],
  healthcare: ['bookings', 'lead_generation', 'messages', 'website_traffic', 'brand_awareness'],
  restaurants: ['sales', 'store_visits', 'messages', 'website_traffic', 'brand_awareness', 'retargeting'],
  automotive: ['lead_generation', 'bookings', 'messages', 'store_visits', 'website_traffic', 'retargeting'],
  tourism: ['bookings', 'lead_generation', 'sales', 'messages', 'website_traffic', 'retargeting'],
  fitness: ['lead_generation', 'bookings', 'sales', 'messages', 'store_visits', 'brand_awareness'],
  mobile_apps: ['website_traffic', 'sales', 'lead_generation', 'brand_awareness', 'retargeting'],
};

const INDUSTRIES: Array<{ value: CampaignIndustry; title: string; description: string }> = [
  { value: 'real_estate', title: 'العقارات', description: 'مشروعات ووحدات ووساطة عقارية' },
  { value: 'ecommerce', title: 'التجارة الإلكترونية', description: 'منتجات ومتاجر وعروض بيع أونلاين' },
  { value: 'professional_services', title: 'الخدمات المهنية', description: 'شركات وخبراء واستشارات وخدمات محلية' },
  { value: 'education', title: 'التعليم والتدريب', description: 'كورسات وأكاديميات ومدارس وبرامج تدريب' },
  { value: 'healthcare', title: 'الصحة والعيادات', description: 'عيادات ومراكز وخدمات صحية ملتزمة بالسياسات' },
  { value: 'restaurants', title: 'المطاعم والكافيهات', description: 'فروع وتوصيل وعروض وتجارب طعام' },
  { value: 'automotive', title: 'السيارات', description: 'بيع وتأجير وصيانة وخدمات سيارات' },
  { value: 'tourism', title: 'السياحة والفنادق', description: 'فنادق ورحلات وتجارب وحجوزات سياحية' },
  { value: 'fitness', title: 'اللياقة البدنية والرياضة', description: 'صالات رياضية وتدريب واشتراكات وخدمات لياقة' },
  { value: 'mobile_apps', title: 'تطبيقات الهاتف والخدمات الرقمية', description: 'تطبيقات ومنتجات SaaS واشتراكات رقمية' },
];

const INDUSTRY_FIELD_LABELS: Record<Exclude<CampaignIndustry, 'real_estate'>, { fulfillment: string; schedule: string; trust: string; averageValue: string }> = {
  ecommerce: { fulfillment: 'الشحن ومدة التوصيل', schedule: 'مدة العرض أو التوفر', trust: 'الضمان وسياسة الاسترجاع وعناصر الثقة', averageValue: 'متوسط قيمة الطلب' },
  professional_services: { fulfillment: 'طريقة تقديم الخدمة: أونلاين أو داخل فرع', schedule: 'مواعيد الحجز أو مدة التنفيذ', trust: 'الخبرات والشهادات وعناصر الثقة', averageValue: 'متوسط قيمة التعاقد' },
  education: { fulfillment: 'مستوى المتعلم وطريقة الدراسة ومدة البرنامج', schedule: 'موعد بدء المجموعة والجدول', trust: 'الشهادة والنتيجة التعليمية الواقعية', averageValue: 'متوسط قيمة الاشتراك' },
  healthcare: { fulfillment: 'طريقة الحجز وموقع تقديم الخدمة', schedule: 'مواعيد العمل المتاحة', trust: 'التراخيص والخبرة وسياسة الخصوصية', averageValue: 'متوسط قيمة الحجز' },
  restaurants: { fulfillment: 'الفروع ونطاق ومدة التوصيل', schedule: 'أوقات العمل وتوفر العرض', trust: 'جودة المكونات وسياسة الطلب', averageValue: 'متوسط سعر الطلب' },
  automotive: { fulfillment: 'موقع المعرض أو المركز وطريقة التسليم', schedule: 'مواعيد الزيارة أو الصيانة', trust: 'الحالة والضمان وشروط التمويل', averageValue: 'متوسط قيمة الطلب أو الحجز' },
  tourism: { fulfillment: 'تفاصيل الإقامة أو الرحلة وما يشمله الحجز', schedule: 'تواريخ السفر والتوفر', trust: 'التراخيص وسياسة الإلغاء وعناصر الثقة', averageValue: 'متوسط قيمة الحجز' },
  fitness: { fulfillment: 'طريقة التدريب وموقعه ومستوى البرنامج', schedule: 'مواعيد الحصص ومدة الاشتراك', trust: 'اعتمادات المدربين وسياسة الاشتراك', averageValue: 'متوسط قيمة الاشتراك' },
  mobile_apps: { fulfillment: 'المنصات وطريقة التفعيل أو الاستخدام', schedule: 'مدة التجربة أو دورة الاشتراك', trust: 'الخصوصية والتقييمات والدعم الفني', averageValue: 'متوسط قيمة الاشتراك' },
};

const STEP_DEFINITIONS = [
  { title: 'بيانات الحملة', short: 'الحملة' },
  { title: 'السوق والعرض', short: 'العرض' },
  { title: 'السعر والتحويل', short: 'التحويل' },
  { title: 'المزايا والجمهور', short: 'الجمهور' },
  { title: 'مراجعة قبل التوليد', short: 'المراجعة' },
];

export const ProjectForm: React.FC<ProjectFormProps> = ({ onSubmit, isLoading, remainingCredits, currentPlan = 'free', brandProfile }) => {
  const { showToast } = useToast();
  const { t, tx, language } = useTranslation();
  const steps = useMemo(
    () => STEP_DEFINITIONS.map((item) => ({ title: tx(item.title), short: tx(item.short) })),
    [language, tx],
  );
  const [step, setStep] = useState(0);
  const [newFeature, setNewFeature] = useState('');
  const [showPresets, setShowPresets] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [generationCost, setGenerationCost] = useState(1);
  const [formData, setFormData] = useState<ProjectFormData>(() => {
    try {
      const draft = JSON.parse(localStorage.getItem('istihdaf_project_draft') || '{}');
      return { ...DEFAULT_DATA, ...draft, industry: draft.industry || 'real_estate' };
    } catch {
      return DEFAULT_DATA;
    }
  });

  const isRealEstate = formData.industry === 'real_estate';
  const campaignGoals = isRealEstate ? REAL_ESTATE_GOALS : GENERAL_GOALS.filter((goal) => (GOALS_BY_INDUSTRY[formData.industry] || []).includes(goal.value));
  const industryFields = isRealEstate ? null : INDUSTRY_FIELD_LABELS[formData.industry];
  const industryPresets = PRESET_TEMPLATES.filter((preset) => preset.data.industry === formData.industry);

  useEffect(() => {
    api.getCreditCosts()
      .then((result: any) => {
        const operation = (result.operations || []).find((item: any) => item.operation_key === 'campaign_generation');
        if (operation) setGenerationCost(Math.max(0, Number(operation.cost_credits || 0)));
      })
      .catch(() => {});
  }, []);

  const creditsAfterGeneration = Math.max(0, remainingCredits - generationCost);
  const campaignCapacityAfter = generationCost > 0 ? Math.floor(creditsAfterGeneration / generationCost) : null;
  const benchmark = INDUSTRY_BENCHMARKS[formData.industry];

  useEffect(() => {
    const timer = window.setTimeout(() => {
      localStorage.setItem('istihdaf_project_draft', JSON.stringify(formData));
      setSavedAt(new Intl.DateTimeFormat(language === 'ar' ? 'ar-EG' : 'en-US', {
        hour: 'numeric',
        minute: '2-digit',
      }).format(new Date()));
    }, 500);
    return () => clearTimeout(timer);
  }, [formData, language]);

  const progress = ((step + 1) / steps.length) * 100;
  const qualityChecks = useMemo(() => [
    { label: tx(isRealEstate ? 'اسم المشروع' : 'اسم المنتج أو الخدمة'), impact: tx('ضروري لبناء الحملة وحفظها باسم واضح'), complete: formData.projectName.trim().length >= 2 },
    { label: tx(isRealEstate ? 'المطور ونوع الوحدة' : 'العلامة ووصف العرض'), impact: tx('يحسّن زاوية الرسالة ويقلل الافتراضات'), complete: Boolean(formData.developerName.trim() && formData.unitType.trim()) },
    { label: tx(isRealEstate ? 'الموقع التفصيلي' : 'السوق المستهدف'), impact: tx('يحسّن تقسيم الجمهور وتقدير تكلفة الليد'), complete: Boolean(formData.region && (isRealEstate ? formData.subArea?.trim() : formData.targetAudience?.trim())) },
    { label: tx(isRealEstate ? 'السعر وخطة السداد' : 'السعر ومسار التحويل'), impact: tx(isRealEstate ? 'يحدد القوة الشرائية والرسالة الاستثمارية' : 'يوضح قيمة العرض والإجراء المطلوب من العميل'), complete: formData.startingPrice > 0 && (isRealEstate ? formData.installmentYears > 0 : Boolean(formData.conversionChannel)) },
    { label: tx(isRealEstate ? 'مميزات المشروع' : 'مزايا العرض'), impact: tx('تمنح النسخ الإعلانية أسبابًا حقيقية للإقناع'), complete: formData.keyFeatures.length >= 2 },
    { label: tx('هدف الحملة والميزانية'), impact: tx('يحدد القناة والإجراء المطلوب من الجمهور'), complete: Boolean(formData.campaignGoal && formData.monthlyBudgetEGP && formData.monthlyBudgetEGP > 0) },
  ], [formData, isRealEstate, tx]);
  const qualityScore = Math.round((qualityChecks.filter((item) => item.complete).length / qualityChecks.length) * 100);
  const missingChecks = qualityChecks.filter((item) => !item.complete);
  const update = <K extends keyof ProjectFormData>(key: K, value: ProjectFormData[K]) => {
    setFormData((previous) => ({ ...previous, [key]: value }));
  };

  const selectIndustry = (industry: CampaignIndustry) => {
    setFormData((previous) => ({
      ...previous,
      industry,
      campaignGoal: industry === 'real_estate' ? 'launch_sales' : 'lead_generation',
      offerType: industry === 'real_estate' ? '' : previous.offerType,
      region: industry === 'real_estate'
        ? (EGYPT_REGIONS.includes(previous.region) ? previous.region : EGYPT_REGIONS[0])
        : (EGYPT_REGIONS.includes(previous.region) ? 'مصر' : previous.region),
    }));
    if (industry !== 'real_estate') setShowPresets(false);
  };

  const applyPreset = (presetData: ProjectFormData) => {
    setFormData({
      ...presetData,
      campaignGoal: presetData.campaignGoal || (presetData.industry === 'real_estate' ? 'launch_sales' : 'lead_generation'),
      projectName: tx(presetData.projectName),
      developerName: tx(presetData.developerName),
      subArea: presetData.subArea ? tx(presetData.subArea) : '',
      unitType: tx(presetData.unitType),
      deliveryYear: tx(presetData.deliveryYear),
      keyFeatures: presetData.keyFeatures.map((feature) => tx(feature)),
      offerType: presetData.offerType ? tx(presetData.offerType) : '',
      targetAudience: presetData.targetAudience ? tx(presetData.targetAudience) : '',
      customerPainPoint: presetData.customerPainPoint ? tx(presetData.customerPainPoint) : '',
      promotionDetails: presetData.promotionDetails ? tx(presetData.promotionDetails) : '',
      fulfillmentDetails: presetData.fulfillmentDetails ? tx(presetData.fulfillmentDetails) : '',
      trustSignals: presetData.trustSignals ? tx(presetData.trustSignals) : '',
      scheduleDetails: presetData.scheduleDetails ? tx(presetData.scheduleDetails) : '',
    });
    setShowPresets(false);
  };

  const getStepError = (targetStep: number): string | null => {
    if (targetStep === 0) {
      if (formData.projectName.trim().length < 2) return tx('اكتب اسمًا واضحًا للحملة أو العرض');
      if (!formData.developerName.trim()) return tx(isRealEstate ? 'اكتب اسم المطور' : 'اكتب اسم العلامة أو الشركة');
      if (!isRealEstate && !formData.offerType?.trim()) return tx('اكتب نوع المنتج أو الخدمة');
    }
    if (targetStep === 1) {
      if (!formData.region.trim()) return tx('حدد السوق أو المنطقة المستهدفة');
      if (!formData.subArea?.trim()) return tx(isRealEstate ? 'حدد المنطقة الفرعية لرفع دقة الاستهداف' : 'حدد المدينة أو نطاق الخدمة');
      if (formData.unitType.trim().length < 8) return tx(isRealEstate ? 'اكتب تفاصيل أوضح عن الوحدة' : 'اكتب وصفًا أوضح للعرض');
    }
    if (targetStep === 2) {
      if (!Number.isFinite(formData.startingPrice) || formData.startingPrice <= 0) return tx('أدخل سعرًا صحيحًا أكبر من صفر');
      if (isRealEstate && (!formData.installmentYears || formData.installmentYears <= 0)) return tx('حدد مدة التقسيط');
      if (!isRealEstate && !formData.conversionChannel) return tx('حدد مسار التحويل المطلوب');
      if (formData.campaignGoal === 'bookings' && !formData.scheduleDetails?.trim()) {
        return tx('هدف "حجوزات ومواعيد" يحتاج تفاصيل المواعيد أو الجدول المتاح');
      }
    }
    if (targetStep === 3) {
      if (formData.keyFeatures.filter((feature) => feature.trim()).length < 2) return tx('أضف ميزتين حقيقيتين على الأقل');
      if (!isRealEstate && !formData.targetAudience?.trim()) return tx('اكتب وصفًا واضحًا للجمهور المستهدف');
      if (!formData.monthlyBudgetEGP || formData.monthlyBudgetEGP <= 0) return tx('أدخل ميزانية شهرية صحيحة');
      if (['retargeting', 'remaining_inventory'].includes(formData.campaignGoal) && !formData.trustSignals?.trim()) {
        return tx('هذا الهدف يحتاج توضيح مين هم العملاء السابقون أو ما الذي تغيّر لمعالجة اعتراضهم');
      }
      if (formData.campaignGoal === 'gulf_investors' && !formData.trustSignals?.trim()) {
        return tx('حملة المستثمرين الخليجيين تحتاج عناصر ثقة واضحة (سجل تسليم سابق، تراخيص، ضمانات)');
      }
      if (['website_traffic', 'sales'].includes(formData.campaignGoal) && !isRealEstate && !formData.websiteUrl?.trim()) {
        return tx('هذا الهدف يحتاج رابط الموقع أو صفحة المنتج ليتم توجيه الزيارات إليها');
      }
    }
    return null;
  };

  const goToNextStep = () => {
    const error = getStepError(step);
    if (error) {
      showToast(error, 'error');
      return;
    }
    setStep((current) => Math.min(steps.length - 1, current + 1));
    document.getElementById('project-form-anchor')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (step < steps.length - 1) {
      goToNextStep();
      return;
    }
    const firstError = [0, 1, 2, 3].map(getStepError).find(Boolean);
    if (firstError) {
      showToast(firstError, 'error');
      return;
    }
    onSubmit({ ...formData, brandProfile });
  };

  const addFeature = () => {
    const feature = newFeature.trim();
    if (!feature) return;
    update('keyFeatures', [...formData.keyFeatures, feature]);
    setNewFeature('');
  };

  const formIntro = (title: string, description: string) => (
    <div className="campaign-form-intro">
      <h3>{tx(title)}</h3>
      <p>{tx(description)}</p>
    </div>
  );

  return (
    <div className="campaign-builder">
      <div className="campaign-builder-toolbar">
        {industryPresets.length ? (
          <button
            type="button"
            onClick={() => setShowPresets((visible) => !visible)}
            className="campaign-template-toggle"
            aria-expanded={showPresets}
          >
            <FileText className="w-4 h-4" />
            {tx(isRealEstate ? 'ابدأ من نموذج عقاري جاهز' : 'ابدأ من نموذج جاهز لمجالك')}
          </button>
        ) : <span className="campaign-template-toggle is-static"><FileText className="w-4 h-4" /> {tx('حملة متعددة المجالات')}</span>}
        <p className="campaign-draft-status" aria-live="polite">
          <Check className="w-3.5 h-3.5" />
          {savedAt ? `${tx('حُفظت المسودة تلقائيًا')} ${savedAt}` : tx('الحفظ التلقائي مفعّل')}
        </p>
      </div>

      {showPresets && (
        <section className="campaign-template-panel animate-slideUp" aria-label={tx('ابدأ من نموذج جاهز')}>
          {industryPresets.map((preset) => (
            <button
              key={preset.id}
              type="button"
              onClick={() => applyPreset(preset.data)}
              className="campaign-template-row"
            >
              <span>
                <strong>{tx(preset.titleAr)}</strong>
                <small>{tx(preset.subtitleAr)}</small>
              </span>
              {preset.badge && <em>{tx(preset.badge)}</em>}
            </button>
          ))}
        </section>
      )}

      <section className="campaign-quality-panel" aria-label={tx('جودة بيانات الحملة')}>
        <div className="campaign-quality-score">
          <span>{tx('جودة البيانات')}</span>
          <strong>{qualityScore}%</strong>
          <div aria-hidden="true"><i style={{ width: `${qualityScore}%` }} /></div>
        </div>
        <div className="campaign-quality-message" role="status">
          {missingChecks.length === 0 ? (
            <><Check /> <span><strong>{tx('البيانات جاهزة للتوليد')}</strong><small>{tx('ستُبنى الاستراتيجية على تفاصيل مكتملة.')}</small></span></>
          ) : (
            <><AlertCircle /> <span><strong>{tx('يمكن تحسين دقة النتيجة')}</strong><small>{missingChecks[0].label}: {missingChecks[0].impact}</small></span></>
          )}
        </div>
        <details className="campaign-quality-details">
          <summary>{tx('عرض فحص البيانات')}</summary>
          <ul>
            {qualityChecks.map((item) => (
              <li key={item.label} className={item.complete ? 'is-complete' : ''}>
                <span>{item.complete ? <Check /> : <AlertCircle />}</span>
                <div><strong>{item.label}</strong><small>{item.impact}</small></div>
              </li>
            ))}
          </ul>
        </details>
      </section>

      <section className="campaign-benchmark-card" aria-label={tx('مؤشر تكلفة النتيجة للمجال')}>
        <TrendingUp />
        <div><span>{tx('نطاق تخطيط مبدئي لـ')} {tx(benchmark.resultLabel)}</span><strong>{benchmark.estimatedRange}</strong><small>{tx('فترة التقييم المقترحة')}: {tx(benchmark.optimizationWindow)} · {tx('تقدير اختباري وليس ضمانًا أو بيانات حية من منصة الإعلانات.')}</small></div>
      </section>

      <form onSubmit={submit} className="campaign-form">
        <header className="campaign-form-header">
          <div className="campaign-form-heading">
            <div>
              <span>{tx('الخطوة')} {step + 1} {tx('من')} {steps.length}</span>
              <h2>{steps[step].title}</h2>
            </div>
            <p>{remainingCredits} {tx('رصيد متاح')}</p>
          </div>

          <div className="campaign-progress" aria-hidden="true">
            <span style={{ width: `${progress}%` }} />
          </div>

          <ol className="campaign-step-list">
            {steps.map((item, index) => (
              <li key={item.short}>
                <button
                  type="button"
                  onClick={() => index < step && setStep(index)}
                  disabled={index > step}
                  className={index === step ? 'is-current' : index < step ? 'is-complete' : ''}
                  aria-current={index === step ? 'step' : undefined}
                >
                  <span>{index < step ? <Check /> : index + 1}</span>
                  <small>{item.short}</small>
                </button>
              </li>
            ))}
          </ol>
        </header>

        <div className="campaign-form-body animate-fadeIn" key={step}>
          {step === 0 && (
            <section className="campaign-form-section">
              {formIntro('لنبدأ بأساسيات الحملة', 'اختر المجال أولاً ليبني الذكاء الاصطناعي الاستراتيجية والأسئلة والسياسات المناسبة له.')}
              <div>
                <label className="field-label">{tx('ما مجال الحملة؟')} <span className="required-mark">*</span></label>
                <div className="campaign-goal-list campaign-industry-list">
                  {INDUSTRIES.map((industry) => (
                    <button
                      type="button"
                      key={industry.value}
                      onClick={() => selectIndustry(industry.value)}
                      className={formData.industry === industry.value ? 'is-selected' : ''}
                    >
                      <span>{formData.industry === industry.value ? <Check /> : null}</span>
                      <div><strong>{tx(industry.title)}</strong><small>{tx(industry.description)}</small></div>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="field-label">{tx('ما الهدف الأساسي من الحملة؟')}</label>
                <div className="campaign-goal-list">
                  {campaignGoals.map((goal) => (
                    <button
                      type="button"
                      key={goal.value}
                      onClick={() => update('campaignGoal', goal.value)}
                      className={formData.campaignGoal === goal.value ? 'is-selected' : ''}
                    >
                      <span>{formData.campaignGoal === goal.value ? <Check /> : null}</span>
                      <div><strong>{tx(goal.title)}</strong><small>{tx(goal.description)}</small></div>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="field-label">{tx(isRealEstate ? 'اسم المشروع' : 'اسم المنتج أو الخدمة')} <span className="required-mark">*</span></label>
                <input autoFocus className="field" value={formData.projectName} onChange={(event) => update('projectName', event.target.value)} placeholder={isRealEstate ? t('form.projectNamePlaceholder') : tx('مثال: باقة إدارة حسابات التواصل')} />
              </div>
              <div>
                <label className="field-label">{tx(isRealEstate ? 'اسم المطور' : 'اسم العلامة أو الشركة')}</label>
                <input className="field" value={formData.developerName} onChange={(event) => update('developerName', event.target.value)} placeholder={isRealEstate ? t('form.developerNamePlaceholder') : tx('مثال: اسم نشاطك التجاري')} />
              </div>
              {isRealEstate ? (
                <div>
                  <label className="field-label">{tx('نوع المشروع')}</label>
                  <div className="choice-grid">
                    {([['residential', 'سكني'], ['commercial_admin', 'تجاري / إداري'], ['coastal_resort', 'ساحلي'], ['land_invest', 'أرض استثمارية']] as [PropertyType, string][]).map(([value, text]) => (
                      <button type="button" key={value} onClick={() => update('propertyType', value)} className={formData.propertyType === value ? 'selected' : ''}>{tx(text)}</button>
                    ))}
                  </div>
                </div>
              ) : (
                <div>
                  <label className="field-label">{tx('نوع المنتج أو الخدمة')}</label>
                  <input className="field" value={formData.offerType || ''} onChange={(event) => update('offerType', event.target.value)} placeholder={tx('مثال: كورس أونلاين، خدمة استشارية، منتج تجميلي...')} />
                </div>
              )}
            </section>
          )}

          {step === 1 && (
            <section className="campaign-form-section">
              {formIntro(isRealEstate ? 'أين يقع المشروع؟' : 'أين يوجد جمهورك وما العرض؟', isRealEstate ? 'الموقع ونوع الوحدة يحددان الجمهور ونطاق الاستهداف.' : 'السوق المستهدف ووصف العرض يساعدان على بناء جمهور ورسالة أكثر دقة.')}
              <div className="campaign-field-grid">
                <div>
                  <label className="field-label">{tx(isRealEstate ? 'المنطقة الرئيسية' : 'الدولة أو السوق المستهدف')}</label>
                  {isRealEstate ? (
                    <select className="field" value={formData.region} onChange={(event) => update('region', event.target.value)}>
                      {EGYPT_REGIONS.map((region) => <option key={region} value={region}>{tx(region)}</option>)}
                    </select>
                  ) : (
                    <input className="field" value={formData.region} onChange={(event) => update('region', event.target.value)} placeholder={tx('مثال: مصر، القاهرة والجيزة، أو دول الخليج')} />
                  )}
                </div>
                <div>
                  <label className="field-label">{tx(isRealEstate ? 'المنطقة الفرعية' : 'المدينة أو نطاق التوصيل')}</label>
                  <input className="field" value={formData.subArea || ''} onChange={(event) => update('subArea', event.target.value)} placeholder={isRealEstate ? t('form.subAreaPlaceholder') : tx('مثال: القاهرة الكبرى أو نطاق 10 كم من الفرع')} />
                </div>
              </div>
              <div>
                <label className="field-label">{tx(isRealEstate ? 'تفاصيل الوحدة' : 'وصف المنتج أو الخدمة والعرض')}</label>
                <textarea className="field" rows={3} value={formData.unitType} onChange={(event) => update('unitType', event.target.value)} placeholder={isRealEstate ? t('form.unitDetailsPlaceholder') : tx('اكتب ما سيحصل عليه العميل وأهم تفاصيل العرض باختصار')} />
              </div>
              {!isRealEstate && industryFields && (
                <div className="campaign-field-grid">
                  <div>
                    <label className="field-label">{tx(industryFields.fulfillment)}</label>
                    <input
                      className="field"
                      value={formData.fulfillmentDetails || ''}
                      onChange={(event) => update('fulfillmentDetails', event.target.value)}
                      placeholder={tx('اكتب طريقة التنفيذ أو التوصيل بوضوح')}
                    />
                  </div>
                  <div>
                    <label className="field-label">{tx('رابط صفحة العرض أو التطبيق')}</label>
                    <input
                      className="field"
                      type="url"
                      value={formData.websiteUrl || ''}
                      onChange={(event) => update('websiteUrl', event.target.value)}
                      placeholder="https://"
                    />
                  </div>
                </div>
              )}
            </section>
          )}

          {step === 2 && (
            <section className="campaign-form-section">
              {formIntro(isRealEstate ? 'السعر وخطة السداد' : 'السعر ومسار التحويل', isRealEstate ? 'نستخدمها لتقدير القوة الشرائية وجودة الجمهور وCPL.' : 'حدد سعر العرض والطريق الذي تريد أن يتخذه العميل بعد مشاهدة الإعلان.')}
              <div className="campaign-field-grid">
                <div>
                  <label className="field-label">{tx(isRealEstate ? 'السعر يبدأ من' : 'سعر المنتج أو الخدمة')}</label>
                  <input type="number" min="0" className="field" value={formData.startingPrice} onChange={(event) => update('startingPrice', Number(event.target.value))} />
                </div>
                <div>
                  <label className="field-label">{tx('العملة')}</label>
                  <select className="field" value={formData.currency} onChange={(event) => update('currency', event.target.value as ProjectFormData['currency'])}>
                    <option>EGP</option><option>USD</option><option>SAR</option><option>AED</option>
                  </select>
                </div>
              </div>
              {isRealEstate ? (
                <div className="campaign-field-grid is-three-columns">
                  <div>
                    <label className="field-label">{tx('المقدم %')}</label>
                    <input type="number" min="0" max="100" className="field" value={formData.downPaymentPercent} onChange={(event) => update('downPaymentPercent', Number(event.target.value))} />
                  </div>
                  <div>
                    <label className="field-label">{tx('سنوات التقسيط')}</label>
                    <input type="number" min="0" className="field" value={formData.installmentYears} onChange={(event) => update('installmentYears', Number(event.target.value))} />
                  </div>
                  <div>
                    <label className="field-label">{tx('موعد التسليم')}</label>
                    <input className="field" value={formData.deliveryYear} onChange={(event) => update('deliveryYear', event.target.value)} />
                  </div>
                </div>
              ) : (
                <>
                  <div>
                    <label className="field-label">{tx('تفاصيل العرض أو الخصم')}</label>
                    <input className="field" value={formData.promotionDetails || ''} onChange={(event) => update('promotionDetails', event.target.value)} placeholder={tx('مثال: خصم 20% حتى نهاية الشهر أو جلسة تعريفية مجانية')} />
                  </div>
                  <div className="campaign-field-grid">
                    <div>
                      <label className="field-label">{tx('مسار التحويل المطلوب')}</label>
                      <select className="field" value={formData.conversionChannel || 'whatsapp'} onChange={(event) => update('conversionChannel', event.target.value as ProjectFormData['conversionChannel'])}>
                        <option value="website">{tx('شراء أو تسجيل من الموقع')}</option>
                        <option value="whatsapp">{tx('محادثة واتساب')}</option>
                        <option value="messages">{tx('رسائل السوشيال ميديا')}</option>
                        <option value="calls">{tx('مكالمة هاتفية')}</option>
                        <option value="store">{tx('زيارة الفرع')}</option>
                      </select>
                    </div>
                    <div>
                      <label className="field-label">{tx('المشكلة التي يحلها العرض')}</label>
                      <input className="field" value={formData.customerPainPoint || ''} onChange={(event) => update('customerPainPoint', event.target.value)} placeholder={tx('ما المشكلة أو الرغبة الأساسية لدى العميل؟')} />
                    </div>
                  </div>
                  {industryFields && (
                    <div className="campaign-field-grid">
                      <div>
                        <label className="field-label">{tx(industryFields.averageValue)}</label>
                        <input
                          className="field"
                          type="number"
                          min="0"
                          value={formData.averageOrderValue || 0}
                          onChange={(event) => update('averageOrderValue', Number(event.target.value))}
                        />
                      </div>
                      <div>
                        <label className="field-label">{tx(industryFields.schedule)}</label>
                        <input
                          className="field"
                          value={formData.scheduleDetails || ''}
                          onChange={(event) => update('scheduleDetails', event.target.value)}
                          placeholder={tx('اكتب المواعيد أو مدة التوفر')}
                        />
                      </div>
                    </div>
                  )}
                </>
              )}
            </section>
          )}

          {step === 3 && (
            <section className="campaign-form-section">
              {formIntro('الجمهور والرسالة', 'آخر خطوة قبل بناء الاستراتيجية والنسخ الإعلانية.')}
              <div>
                <label className="field-label">{tx(isRealEstate ? 'أهم مميزات المشروع' : 'أهم مزايا المنتج أو الخدمة')}</label>
                <div className="campaign-feature-entry">
                  <input className="field" value={newFeature} onChange={(event) => setNewFeature(event.target.value)} onKeyDown={(event) => event.key === 'Enter' && (event.preventDefault(), addFeature())} placeholder={tx(isRealEstate ? 'مثال: موقع استراتيجي، مقدم 10%...' : 'مثال: توصيل سريع، ضمان، خبرة موثقة...')} />
                  <button type="button" onClick={addFeature} className="btn btn-secondary">{tx('إضافة')}</button>
                </div>
                {formData.keyFeatures.length > 0 && (
                  <div className="campaign-feature-list">
                    {formData.keyFeatures.map((feature, index) => (
                      <button type="button" key={`${feature}-${index}`} onClick={() => update('keyFeatures', formData.keyFeatures.filter((_, featureIndex) => featureIndex !== index))}>{feature} ×</button>
                    ))}
                  </div>
                )}
              </div>
              {!isRealEstate && industryFields && (
                <div>
                  <label className="field-label">{tx(industryFields.trust)}</label>
                  <textarea
                    className="field"
                    rows={3}
                    value={formData.trustSignals || ''}
                    onChange={(event) => update('trustSignals', event.target.value)}
                    placeholder={tx('اذكر فقط شهادات أو ضمانات أو أدلة حقيقية يمكن إثباتها')}
                  />
                </div>
              )}
              <div className="campaign-field-grid">
                <div>
                  <label className="field-label">{tx(isRealEstate ? 'الجمهور المبدئي' : 'وصف الجمهور المستهدف')}</label>
                  {isRealEstate ? (
                    <select className="field" value={formData.audienceType} onChange={(event) => update('audienceType', event.target.value as AudienceType)}>
                      <option value="egyptian_investor">{tx('مستثمر مصري')}</option>
                      <option value="gulf_expat">{tx('مصريون بالخليج')}</option>
                      <option value="family_home">{tx('أسرة تبحث عن سكن')}</option>
                      <option value="business_medical">{tx('أعمال وعيادات')}</option>
                      <option value="luxury_vacation">{tx('رفاهية ومصايف')}</option>
                    </select>
                  ) : (
                    <input className="field" value={formData.targetAudience || ''} onChange={(event) => update('targetAudience', event.target.value)} placeholder={tx('مثال: أصحاب مشروعات صغيرة في القاهرة من 25 إلى 45 سنة')} />
                  )}
                </div>
                <div>
                  <label className="field-label">{tx('أسلوب الكتابة')}</label>
                  <select className="field" value={formData.dialectStyle} onChange={(event) => update('dialectStyle', event.target.value as DialectStyle)}>
                    <option value="egyptian_colloquial">{tx('عامية مصرية')}</option>
                    <option value="modern_standard_arabic">{tx('عربية فصحى حديثة')}</option>
                    <option value="hybrid">{tx('مزيج احترافي')}</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="field-label">{tx('الميزانية الشهرية المتوقعة')}</label>
                <input type="number" min="0" className="field" value={formData.monthlyBudgetEGP || 0} onChange={(event) => update('monthlyBudgetEGP', Number(event.target.value))} />
              </div>
            </section>
          )}

          {step === 4 && (
            <section className="campaign-review-step">
              {formIntro('راجع البيانات قبل استخدام الرصيد', 'لن يبدأ التوليد إلا بعد تأكيدك. يمكنك الرجوع لأي خطوة وتصحيح البيانات بدون خصم رصيد.')}
              <div className="campaign-review-readiness">
                <span><Check /></span>
                <div><strong>{tx('الحملة جاهزة للتوليد')}</strong><small>{tx('جودة البيانات')} {qualityScore}% · {tx('ستستهلك')} {generationCost} {tx('كريدت')} · {tx('سيصبح رصيدك')} {creditsAfterGeneration}</small></div>
              </div>
              {campaignCapacityAfter !== null && (
                <div className="campaign-credit-preview"><TrendingUp /><span>{tx('بعد التنفيذ سيتبقى لديك ما يكفي لإنشاء')} <strong>{campaignCapacityAfter}</strong> {tx('حملات كاملة تقريباً')}.</span></div>
              )}
              <dl className="campaign-review-summary">
                <div><dt>{tx('المجال')}</dt><dd>{tx(INDUSTRIES.find((industry) => industry.value === formData.industry)?.title || '')}</dd></div>
                <div><dt>{tx('الهدف')}</dt><dd>{tx(campaignGoals.find((goal) => goal.value === formData.campaignGoal)?.title || '')}</dd></div>
                <div><dt>{tx(isRealEstate ? 'المشروع' : 'العرض')}</dt><dd>{formData.projectName}</dd></div>
                <div><dt>{tx('السوق')}</dt><dd>{formData.region} · {formData.subArea}</dd></div>
                <div><dt>{tx('السعر')}</dt><dd dir="ltr">{formData.startingPrice.toLocaleString(language === 'ar' ? 'ar-EG' : 'en-US')} {formData.currency}</dd></div>
                <div><dt>{tx('الميزانية الشهرية')}</dt><dd dir="ltr">{(formData.monthlyBudgetEGP || 0).toLocaleString(language === 'ar' ? 'ar-EG' : 'en-US')} EGP</dd></div>
                <div className="is-wide"><dt>{tx('الجمهور')}</dt><dd>{isRealEstate ? tx(formData.audienceType) : formData.targetAudience}</dd></div>
                <div className="is-wide"><dt>{tx('المزايا المعتمدة')}</dt><dd>{formData.keyFeatures.join(' · ')}</dd></div>
              </dl>
              <div className="campaign-review-assumptions">
                <AlertCircle />
                <p><strong>{tx('قبل المتابعة')}</strong><span>{tx('تقديرات التكلفة والنتائج نقاط بداية للاختبار وليست ضمانًا. لن يخترع المولّد أسعارًا أو عروضًا غير موجودة في البيانات أعلاه.')}</span></p>
              </div>
            </section>
          )}
        </div>

        <footer className="campaign-form-actions">
          <button type="button" onClick={() => setStep((current) => Math.max(0, current - 1))} disabled={step === 0 || isLoading} className="btn btn-ghost">
            <ArrowRight className="w-4 h-4 rtl-mirror" /> {tx('السابق')}
          </button>
          {step < steps.length - 1 ? (
            <button type="submit" className="btn btn-primary">
              {tx('التالي')} <ArrowLeft className="w-4 h-4 rtl-mirror" />
            </button>
          ) : (
            <button type="submit" disabled={isLoading} className="btn btn-primary">
              {isLoading ? <><span className="spinner-soft" /> {tx('جاري بناء الاستراتيجية...')}</> : <>{tx('إنشاء الاستراتيجية بالذكاء الاصطناعي')} · {generationCost} {tx('كريدت')}</>}
            </button>
          )}
        </footer>
      </form>
    </div>
  );
};
